<script setup lang="ts">
/**
 * The pre-chat form, built from `widget_prechat_field` ROWS.
 *
 * NOTHING IN THIS FILE NAMES A FIELD. There is no `name`, no `email`, no
 * `message` — the labels, the order, the requiredness and the option lists all
 * arrive from the API, which generates the zod schema that validates the answers
 * from the SAME rows (E9, gate G-57). That is the whole reason the form is rows
 * and not a Json IR blob: an IR that only one hand-written interpreter
 * understands drifts from the validator it is supposed to be enforced by, and the
 * drift is invisible until a brand edits their form and the API starts rejecting
 * what this screen accepted.
 *
 * The consequence is that this component must be able to render a field type it
 * has never heard of. It does — `renderType` falls back to a text input rather
 * than skipping the field, because a required field that does not render is a
 * form nobody can submit and nobody can debug.
 */
import { computed, ref, shallowRef } from 'vue';

import type { WidgetPrechatField } from '../api/contract';
import {
  initialPrechatValues,
  PRECHAT_ANSWER_MAX_LENGTH,
  renderType,
  validatePrechat,
  type PrechatValues,
} from '../prechat/fields';
import { useText } from '../vendor/i18n';
import { OwButton, OwInput, OwTextarea } from '../vendor/ui';

const props = defineProps<{
  readonly fields: readonly WidgetPrechatField[];
  readonly submitting: boolean;
}>();

const emit = defineEmits<{ submit: [values: PrechatValues] }>();

const text = useText();
// Replaced whole on every keystroke rather than mutated, so `validatePrechat`
// is handed the same plain record its tests exercise.
const values = shallowRef<PrechatValues>(initialPrechatValues(props.fields));
const showErrors = ref(false);

const errors = computed(() => validatePrechat(props.fields, values.value));

function set(key: string, value: string): void {
  values.value = { ...values.value, [key]: value };
}

function submit(): void {
  // Errors appear on SUBMIT, not on every keystroke. Marking a required field
  // red before the visitor has finished typing their name is the interaction
  // everyone hates and nobody defends.
  if (Object.keys(errors.value).length > 0) {
    showErrors.value = true;
    return;
  }
  emit('submit', values.value);
}

function errorOf(key: string): ReturnType<typeof validatePrechat>[string] | undefined {
  return showErrors.value ? errors.value[key] : undefined;
}

function errorText(key: string): string {
  const error = errorOf(key);
  return error === undefined ? '' : text(error);
}

function describedBy(key: string): string | undefined {
  return errorOf(key) === undefined ? undefined : `ow-field-${key}-error`;
}

function inputType(type: string): 'email' | 'tel' | 'text' {
  const known = renderType(type);
  return known === 'email' ? 'email' : known === 'phone' ? 'tel' : 'text';
}

function autocomplete(type: string): string {
  const known = renderType(type);
  return known === 'email' ? 'email' : known === 'phone' ? 'tel' : 'on';
}
</script>

<template>
  <!--
    The browser's own validation bubbles are unstyled, untranslated and
    positioned relative to a frame the visitor cannot see the edges of.
    `novalidate` plus our own messages keeps them inside the panel and in the
    visitor's language.
  -->
  <form class="ow-prechat" novalidate @submit.prevent="submit">
    <p class="ow-prechat__intro">{{ text('widget.prechat.intro') }}</p>

    <div v-for="field in fields" :key="field.key" class="ow-field">
      <label class="ow-field__label" :for="`ow-field-${field.key}`">
        {{ field.label
        }}<span v-if="!field.required" class="ow-field__optional">{{
          ` (${text('widget.prechat.optional')})`
        }}</span>
      </label>

      <!--
        A native `<select>`, not a design-system compound one. This panel is
        opened on a phone more often than not, so the platform's own picker is
        the better control.

        The option VALUE is the option label: `options` is a list of strings on
        the wire, and inventing a {value,label} pair here would be this app
        holding an opinion the database does not.
      -->
      <select
        v-if="renderType(field.type) === 'select'"
        :id="`ow-field-${field.key}`"
        class="ow-field__control ow-field__select"
        :value="values[field.key] ?? ''"
        :required="field.required"
        :aria-invalid="errorOf(field.key) === undefined ? undefined : true"
        :aria-describedby="describedBy(field.key)"
        @change="set(field.key, ($event.target as HTMLSelectElement).value)"
      >
        <option value="">{{ text('widget.select.placeholder') }}</option>
        <option v-for="option in field.options" :key="option" :value="option">
          {{ option }}
        </option>
      </select>

      <OwTextarea
        v-else-if="renderType(field.type) === 'textarea'"
        :id="`ow-field-${field.key}`"
        class="ow-field__control"
        :model-value="values[field.key] ?? ''"
        :rows="3"
        :required="field.required"
        :invalid="errorOf(field.key) !== undefined"
        :maxlength="PRECHAT_ANSWER_MAX_LENGTH"
        :described-by="describedBy(field.key)"
        @update:model-value="set(field.key, $event)"
      />

      <!--
        Autofill is worth having: the visitor is being asked for a name and an
        email on someone else's checkout page, and the browser already knows
        both. `allow-forms` in the loader's sandbox is what keeps this working
        inside the frame.
      -->
      <OwInput
        v-else
        :id="`ow-field-${field.key}`"
        class="ow-field__control"
        :type="inputType(field.type)"
        :model-value="values[field.key] ?? ''"
        :required="field.required"
        :invalid="errorOf(field.key) !== undefined"
        :maxlength="PRECHAT_ANSWER_MAX_LENGTH"
        :autocomplete="autocomplete(field.type)"
        :described-by="describedBy(field.key)"
        @update:model-value="set(field.key, $event)"
      />

      <p
        v-if="errorOf(field.key) !== undefined"
        :id="`ow-field-${field.key}-error`"
        class="ow-field__error"
      >
        {{ errorText(field.key) }}
      </p>
    </div>

    <OwButton
      type="submit"
      variant="primary"
      block
      :busy="submitting"
      :busy-label="text('widget.conversation.loading')"
    >
      {{ text('widget.prechat.submit') }}
    </OwButton>
  </form>
</template>
